import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import {MatPaginator, MatPaginatorIntl, PageEvent} from "@angular/material/paginator";
import {CustomPaginatorIntl} from "../../../../models/custom-paginator-intl";
import { Pageable } from '../../../../types/pagable.types';
import { RecordData } from '../../../../types/records-table.types';
import { ResourceParams } from '../../../../models/resource/resources';
import { ErrorService } from '../../../../services/error.service';
import { ValidationService } from '../../../../services/validation.service';
import { UserPreferencesService } from '../../../../services/user-preferences.service';
import {
  RecordsDataRequestQueryParamsService
} from "../../../records-table-container/services/records-data-request-query-params.service";
import {firstPageDefault, pageSizeOptions} from '../../../../models/pagination-params';
import {
  defaultRecordsDataRequestQueryParams
} from "../../../../models/default-records-data-request-query-params";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {defaultResourceParams} from "../../../../models/resource/default-resource-params";
import {defaultPageableRecordData} from "../../../../models/default-pageable-record-data";
import {RecordsTablePaginatorService} from "../../../../services/table-services/records-table-paginator.service";
import {NumberInputParams} from "../../../../types/number-input.types";

@Component({
  selector: 'app-records-table-paginator',
  imports: [
    MatPaginator,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './records-table-paginator.component.html',
  styleUrl: './records-table-paginator.component.scss',
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }
  ],
})
export class RecordsTablePaginatorComponent {
  @Input({ required: true }) tableData: Pageable<RecordData> = defaultPageableRecordData;
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Output() recordTableUpdated: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;

  private errorService: ErrorService = inject(ErrorService);
  private validationService: ValidationService = inject(ValidationService);
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private recordsDataRequestQueryParamsService: RecordsDataRequestQueryParamsService = inject(RecordsDataRequestQueryParamsService);
  private recordsTablePaginatorService: RecordsTablePaginatorService = inject(RecordsTablePaginatorService);

  public pageSizeOptions: Array<number> = pageSizeOptions;
  public firstPage: number = firstPageDefault;
  public selectTablePage: number = firstPageDefault;
  public pageSize = this.recordsTablePaginatorService.getDefaultPageSize();

  public changePageParams(event: PageEvent): void {
    this.selectTablePage = event.pageIndex + 1;
    this.recordsDataRequestQueryParamsService.changePageParams(event);
    this.userPreferencesService.saveUserPreferences({pageSize: event.pageSize});
    this.recordTableUpdated.emit();
  }

  public goToPage(page: number): void {
    const countPage = Number(this.tableData.totalPages);
    if (page <= countPage) {
      this.selectTablePage = page;
      this.recordsDataRequestQueryParamsService.goToPage(this.selectTablePage);
      this.recordTableUpdated.emit();
    }
    else {
      this.selectTablePage = Number(this.recordsDataRequestQueryParamsService.recordsDataRequestQueryParamsState()?.page) + 1;
    }
  }

  public resetPaginator(): void {
    if (!this.paginator) { return; }

    this.selectTablePage = this.firstPage;
    this.paginator.pageSize = Number(this.recordsTablePaginatorService.getDefaultPageSize());
    this.paginator.pageIndex = Number(defaultRecordsDataRequestQueryParams.page);
  }

  public isResourceInDropdownSettings(): boolean {
    return this.recordsTablePaginatorService.isResourceInDropdownSettings();
  }

  public preventDecimalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const numberInputConfig = this.getNumberInputConfig(value);

    if (this.validationService.isInvalidNumberInput(numberInputConfig)) {
      this.errorService.showErrorMessage(`Ошибка! Укажите целое число от ${this.firstPage} до ${this.tableData.totalPages}.`);
      input.value = (this.tableData.number + 1).toString();
    }
  }

  public preventInvalidNumericInput(event: KeyboardEvent) {
    this.validationService.preventInvalidNumericInput(event);
  }

  private getNumberInputConfig(value: string): NumberInputParams {
    return {
      value,
      minValue: this.firstPage,
      maxValue: this.tableData.totalPages
    };
  }
}
